/* Created by Interactive 63 - https://interactive63.com */
/**
 * Stagger ordering: decides the order in which a set of elements starts, from layer order,
 * position on screen, distance from the middle, or a repeatable shuffle.
 */
import { seededShuffle } from '../random';

export type StaggerOrder =
  | 'layer'
  | 'reverse'
  | 'left-right'
  | 'right-left'
  | 'top-bottom'
  | 'bottom-top'
  | 'centre-out'
  | 'random';

export const STAGGER_ORDERS: { id: StaggerOrder; label: string }[] = [
  { id: 'layer', label: 'Layer order' },
  { id: 'reverse', label: 'Reverse layer order' },
  { id: 'left-right', label: 'Left to right' },
  { id: 'right-left', label: 'Right to left' },
  { id: 'top-bottom', label: 'Top to bottom' },
  { id: 'bottom-top', label: 'Bottom to top' },
  { id: 'centre-out', label: 'From centre outwards' },
  { id: 'random', label: 'Random' },
];

export interface StaggerItem {
  id: string;
  /** Position in layer order. */
  index: number;
  /** Bounding box centre on screen. */
  centre: { x: number; y: number };
}

/**
 * The element IDs in the order they start. Ties keep layer order, so results are stable.
 * `seed` only matters for the random order.
 */
export function staggerOrder(
  items: readonly StaggerItem[],
  order: StaggerOrder,
  seed = 1,
): string[] {
  const byLayer = [...items].sort((a, b) => a.index - b.index);
  /** Sorts by a number, keeping layer order for equal values. */
  const sortBy = (key: (item: StaggerItem) => number): string[] =>
    [...byLayer].sort((a, b) => key(a) - key(b) || a.index - b.index).map((i) => i.id);

  switch (order) {
    case 'layer':
      return byLayer.map((i) => i.id);
    case 'reverse':
      return byLayer.map((i) => i.id).reverse();
    case 'left-right':
      return sortBy((i) => i.centre.x);
    case 'right-left':
      return sortBy((i) => -i.centre.x);
    case 'top-bottom':
      return sortBy((i) => i.centre.y);
    case 'bottom-top':
      return sortBy((i) => -i.centre.y);
    case 'centre-out': {
      const count = byLayer.length || 1;
      const cx = byLayer.reduce((sum, i) => sum + i.centre.x, 0) / count;
      const cy = byLayer.reduce((sum, i) => sum + i.centre.y, 0) / count;
      return sortBy((i) => Math.hypot(i.centre.x - cx, i.centre.y - cy));
    }
    case 'random':
      return seededShuffle(
        byLayer.map((i) => i.id),
        seed,
      );
  }
}
